import { EditorContent } from '@tiptap/react';
import styled, { css } from 'styled-components';

const SYSTEM_FONT = `-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica,
      'Apple Color Emoji', Arial, sans-serif, 'Segoe UI Emoji',
      'Segoe UI Symbol'`;

const CURSIVE_FONT = `'SACursive', ${SYSTEM_FONT}`;

interface EditorStyleProps {
  $variant?: 'default' | 'cursive';
  $worksheet?: boolean;
}

const editorCSS = css`
  .writing-line {
    border: none;
    border-top: 1px solid #000;
    margin: 2em 0;
    padding: 0;
    height: 1px;
    background-color: transparent;
  }

  &:focus {
    outline: none;
  }

  > * + * {
    margin-top: 0.75em;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-weight: 700;
    margin-bottom: 8px;
  }

  h1 {
    font-size: 24px;
    border-bottom: 1px solid #e0e4e8;
    margin-bottom: 20px;
    padding-bottom: 6px;
    font-weight: 600;
  }

  h2 {
    font-size: 18px;
    font-weight: 600;
    margin-top: 24px;
    margin-bottom: 12px;
  }

  h3 {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 4px;
  }

  p {
    margin-bottom: 4px;
  }

  .horizontal-list {
    background-color: #f7f6f3;
    padding: 12px 16px;
    border-radius: 3px;
    font-style: italic;
    margin-bottom: 1em;
    border: 1px solid #e0e4e8;
  }

  .activity-block {
    position: relative;
    background-color: transparent;
    border: none;
    border-top: 1px solid #e9ecef;
    border-radius: 0;
    padding: 12px 4px 8px 4px;
    cursor: default;
    margin-bottom: 0.5em;
    overflow: hidden;
    transition: background-color 0.2s ease;

    &:first-child {
      border-top: none;
    }

    &:hover {
      background-color: #fafafa;
    }

    &.has-focus {
      background-color: #fafafa;
    }

    h3 {
      font-size: 15px;
      font-weight: 600;
      margin-bottom: 4px;
    }

    .skill,
    .instructions,
    .script,
    .activity-content,
    .additional-notes {
      margin-bottom: 2px;
      font-size: 13px;
      width: 100%;
      color: #37352f;

      strong {
        font-weight: 600;
      }
    }

    .activity-content {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px;
      margin: 4px 0;
    }

    ul,
    ol {
      margin: 2px 0;
      padding-left: 1.5em;

      li {
        font-size: 13px;
        color: #37352f;
        margin-bottom: 2px;
      }
    }
  }

  .content-item {
    font-size: 13px;
    padding: 4px 8px;
    border-radius: 3px;
    border: 1px solid #e9ecef;
    background-color: #ffffff;
    color: #37352f;
  }

  .content.horizontal-layout {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;

    .content-item {
      background-color: #ffffff;
      padding: 4px 8px;
      border-radius: 4px;
      border: 1px solid #e9ecef;
    }
  }

  .word-matrix-content {
    display: flex;
    align-items: stretch;
    border: 2px solid #000;
    margin: 8px 0;
    width: fit-content;

    .word-matrix-prefix-col,
    .word-matrix-suffix-col {
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    .word-matrix-prefix-col + .word-matrix-prefix-col {
      border-left: 1px solid #000;
    }

    .word-matrix-suffix-col + .word-matrix-suffix-col {
      border-left: 1px solid #000;
    }

    .word-matrix-base {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      font-weight: 700;
      padding: 12px 24px;
      border-left: 2px solid #000;
      border-right: 2px solid #000;
      min-height: 40px;
    }

    .prefix,
    .suffix {
      display: flex;
      align-items: center;
      font-size: 13px;
      font-weight: 500;
      padding: 6px 12px;
      border-bottom: 1px solid #000;
      min-width: 40px;

      &:last-child {
        border-bottom: none;
      }
    }

    .prefix {
      justify-content: flex-start;
    }

    .suffix {
      justify-content: flex-end;
    }
  }

  .content.word-chain {
    .content-item {
      display: inline-flex;
      align-items: center;
      background-color: #ffffff;
      padding: 4px 8px;
      border-radius: 4px;
      border: 1px solid #e9ecef;

      &:not(:last-child)::after {
        content: '>';
        margin: 0 6px;
        font-weight: bold;
      }
    }
  }

  mark {
    border-radius: 2px;
    padding: 0 2px;
  }

  ul,
  ol {
    padding-left: 1.5em;
    margin: 1em 0;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  li {
    margin-bottom: 0.5em;
  }

  /* Nested list style progression */
  ul ul {
    list-style-type: circle;
  }
  ul ul ul {
    list-style-type: square;
  }
  ul ul ul ul {
    list-style-type: disc;
  }

  ol ol {
    list-style-type: lower-alpha;
  }
  ol ol ol {
    list-style-type: lower-roman;
  }
  ol ol ol ol {
    list-style-type: decimal;
  }

  /* Cross-nesting resets */
  ol ul {
    list-style-type: disc;
  }
  ul ol {
    list-style-type: decimal;
  }

  [style*='font-family'] {
    font-family: inherit;
  }

  [style*='font-family: Aronui'] {
    font-family: 'Aronui', sans-serif;
  }

  [style*='font-family: Vic/WA/NTCursive'] {
    font-family: 'Vic/WA/NTCursive', sans-serif;
  }

  blockquote {
    border-left: 3px solid #e9ecef;
    margin: 1em 0;
    padding-left: 0.75rem;
  }

  hr {
    border: none;
    border-top: 1px solid #e9ecef;
    margin: 1.5rem 0;
  }

  .column-layout {
    display: flex;
    gap: 60px;
    margin-bottom: 1em;
    width: 100%;
    min-height: 40px;
  }

  .column {
    flex: 1 1 50%;
    min-width: 0;
    border: 1px dashed transparent;
    border-radius: 4px;
    padding: 4px;
    transition: border-color 0.2s ease;
  }

  .column-layout:hover .column,
  .column:focus-within {
    border-color: #e0e4e8;
  }

  .column-drop-indicator-left {
    box-shadow: -4px 0 0 0 #2563eb;
  }

  .column-drop-indicator-right {
    box-shadow: 4px 0 0 0 #2563eb;
  }

  p.is-empty::before {
    content: attr(data-placeholder);
    float: left;
    color: #b3b3b3;
    pointer-events: none;
    height: 0;
  }
`;

const printCSS = css`
  @media print {
    width: 100% !important;
    min-height: auto !important;
    margin: 0 !important;
    box-shadow: none !important;

    .ProseMirror {
      min-height: auto !important;
      padding: 0 !important;

      > * + * {
        margin-top: 0.5em;
      }

      h1 {
        font-size: 20px;
        margin-bottom: 10px;
        padding-bottom: 4px;
      }

      h2 {
        font-size: 15px;
        margin-top: 12px;
        margin-bottom: 6px;
        break-after: avoid;
      }

      h3 {
        font-size: 12px;
        break-after: avoid;
      }

      p {
        margin-bottom: 2px;
        orphans: 3;
        widows: 3;
      }

      .horizontal-list {
        padding: 6px 10px;
        margin-bottom: 0.5em;
      }

      .activity-block {
        break-inside: avoid;
        overflow: visible;
        padding: 8px 4px 6px 4px;
        margin-bottom: 0.25em;
        transition: none;

        &:hover {
          background-color: transparent;
        }

        h3 {
          font-size: 13px;
        }

        .skill,
        .instructions,
        .script,
        .activity-content,
        .additional-notes {
          font-size: 11px;
        }

        ul,
        ol {
          li {
            font-size: 11px;
          }
        }
      }

      .column-layout {
        break-inside: avoid;
        gap: 12px;
        margin-bottom: 0.5em;
        min-height: auto;
      }

      .column {
        border-color: transparent !important;
        padding: 2px;
      }

      .content-item {
        font-size: 11px;
        padding: 2px 6px;
      }

      .content.horizontal-layout {
        gap: 4px;

        .content-item {
          padding: 2px 6px;
        }
      }

      .content.word-chain .content-item {
        padding: 2px 6px;
      }

      .word-matrix-content {
        .word-matrix-base {
          font-size: 16px;
          padding: 8px 16px;
        }

        .prefix,
        .suffix {
          font-size: 11px;
          padding: 4px 8px;
        }
      }

      .writing-line {
        margin: 1em 0;
      }

      blockquote {
        margin: 0.5em 0;
      }

      hr {
        margin: 0.75rem 0;
      }

      ul,
      ol {
        margin: 0.5em 0;
        padding-left: 1.2em;

        li {
          margin-bottom: 0.25em;
        }
      }

      ul ul {
        list-style-type: circle;
      }
      ul ul ul {
        list-style-type: square;
      }
      ol ol {
        list-style-type: lower-alpha;
      }
      ol ol ol {
        list-style-type: lower-roman;
      }

      p.is-empty {
        display: none;
      }
    }
  }
`;

export const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: white;
`;

export const EditorWrapper = styled.div`
  position: relative;
  flex-grow: 1;
  overflow-y: auto;
  background-color: white;
`;

export const StyledEditorContent = styled(EditorContent)<EditorStyleProps>`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  background-color: white;

  .ProseMirror {
    outline: none;
    font-family: ${(props) =>
      props.$variant === 'cursive' ? CURSIVE_FONT : SYSTEM_FONT};
    font-size: ${(props) => (props.$variant === 'cursive' ? '16px' : '13px')};
    line-height: 1.6;
    padding: 32px 48px 120px 80px;

    ${editorCSS}
  }

  ${(props) =>
    props.$worksheet &&
    css`
      .ProseMirror .content-item {
        border: none;
        background-color: transparent;
        padding: 0;
      }

      .ProseMirror .word-matrix-content {
        .word-matrix-base {
          font-size: 32px;
          padding: 16px 40px;
        }

        .prefix,
        .suffix {
          font-size: 18px;
          padding: 10px 20px;
        }
      }

      .ProseMirror .sound-recognition-list {
        margin: 8px 0;
        padding-left: 1.5em;
        columns: 2;
        column-gap: 24px;

        li {
          font-size: 14px;
          margin-bottom: 4px;
          break-inside: avoid;
        }
      }
    `}

  ${printCSS}
`;
